import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import {Lock, CheckCircle, AlertTriangle, Loader2, RefreshCw, ArrowRight, Copy} from 'lucide-react'
import { copiarTexto as copiarParaArea } from '@/utils/clipboard'

// ─── Constantes ───────────────────────────────────────────────────────────────
const EMAIL_FN_URL =
  'https://api.lumi.new/v1/functions/p427216140118241280/send-welcome-email'

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface TokenPayload {
  email: string
  exp: number
}

function decodeTokenCapacitacao(token: string): TokenPayload | null {
  try {
    const decoded = atob(token)
    const parsed = JSON.parse(decoded) as TokenPayload
    if (!parsed.email || !parsed.exp) return null
    return parsed
  } catch {
    return null
  }
}

function gerarNovoTokenCriarSenha(email: string): { token: string; expIso: string } {
  const exp = Date.now() + 1000 * 60 * 60 * 24 * 7
  const token = btoa(JSON.stringify({ email, exp }))
  return { token, expIso: new Date(exp).toISOString() }
}

type ForcaSenha = 'fraca' | 'media' | 'forte' | 'vazia'

function calcularForcaSenha(senha: string): ForcaSenha {
  if (!senha) return 'vazia'
  const temNumero = /\d/.test(senha)
  const temLetra = /[a-zA-Z]/.test(senha)
  const temEspecial = /[^a-zA-Z0-9]/.test(senha)
  const len = senha.length
  if (len < 6 || !temNumero) return 'fraca'
  if (len >= 10 && temLetra && temNumero && temEspecial) return 'forte'
  if (len >= 6 && temLetra && temNumero) return 'media'
  return 'fraca'
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function CriarSenha() {
  const [loading, setLoading] = useState(true)
  const [tokenValido, setTokenValido] = useState(false)
  const [tokenExpirado, setTokenExpirado] = useState(false)
  const [registroId, setRegistroId] = useState<string | null>(null)
  const [nomeParticipante, setNomeParticipante] = useState('')
  const [emailParticipante, setEmailParticipante] = useState('')
  const [whatsappParticipante, setWhatsappParticipante] = useState('')
  const [usuarioGerado, setUsuarioGerado] = useState('')

  // Formulário de senha
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [sucesso, setSucesso] = useState(false)
  const [erro, setErro] = useState('')
  const [copiouDiag, setCopiouDiag] = useState(false)
  const [copiouCap, setCopiouCap] = useState(false)

  // Reenvio
  const [reenvioEmail, setReenvioEmail] = useState('')
  const [reenviando, setReenviando] = useState(false)
  const [reenvioOk, setReenvioOk] = useState(false)
  const [reenvioErro, setReenvioErro] = useState('')

  useEffect(() => {
    validarToken()
  }, [])

  const validarToken = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams(window.location.search)
      const token = params.get('token')
      const emailParam = params.get('email')

      if (!token) {
        setTokenValido(false)
        setLoading(false)
        return
      }

      const payload = decodeTokenCapacitacao(token)
      if (!payload) {
        setTokenValido(false)
        setLoading(false)
        return
      }

      if (Date.now() > payload.exp) {
        setTokenExpirado(true)
        setTokenValido(false)
        setReenvioEmail(payload.email || emailParam || '')
        setLoading(false)
        return
      }

      const emailBusca = payload.email || emailParam || ''
      if (!emailBusca) {
        setTokenValido(false)
        setLoading(false)
        return
      }

      const resp = await lumi.entities.capacitacao.list({
        filter: { email: emailBusca },
        limit: 1,
      })
      const registro = resp?.list?.[0]

      if (!registro) {
        setTokenValido(false)
        setLoading(false)
        return
      }

      const tokenSalvo = registro.tokenCriarSenha || registro.tokenAcesso || ''
      if (!tokenSalvo || tokenSalvo !== token) {
        setTokenValido(false)
        setTokenExpirado(true)
        setReenvioEmail(emailBusca)
        setLoading(false)
        return
      }

      setRegistroId(registro._id)
      setNomeParticipante(registro.nome || '')
      setEmailParticipante(registro.email || '')
      setWhatsappParticipante(registro.whatsapp || '')
      setUsuarioGerado(registro.usuario || '')

      setTokenValido(true)
    } catch (err) {
      console.error('[criar-senha] erro ao validar token:', err)
      setTokenValido(false)
    } finally {
      setLoading(false)
    }
  }

  // ── Reenvio de novo link ──
  const solicitarNovoLink = async () => {
    const email = reenvioEmail.trim().toLowerCase()
    if (!email) {
      setReenvioErro('Informe o e-mail usado na inscrição.')
      return
    }
    setReenviando(true)
    setReenvioErro('')
    try {
      const resp = await lumi.entities.capacitacao.list({
        filter: { email },
        limit: 1,
      })
      const registro = resp?.list?.[0]
      if (!registro) {
        setReenvioErro('Não encontramos uma inscrição com este e-mail. Verifique e tente novamente.')
        setReenviando(false)
        return
      }

      const { token: novoToken, expIso } = gerarNovoTokenCriarSenha(email)
      const agora = new Date().toISOString()
      const linkCriarSenha = `https://www.descubraseusoleos.com.br/criar-senha?token=${encodeURIComponent(novoToken)}&email=${encodeURIComponent(email)}`

      await lumi.entities.capacitacao.update(registro._id, {
        tokenCriarSenha: novoToken,
        tokenCriarSenhaExpiracao: expIso,
        updatedAt: agora,
      })

      const usuarioDoRegistro = registro.usuario || ''
      const linkPersonalizado = usuarioDoRegistro
        ? `https://www.descubraseusoleos.com.br?c=${usuarioDoRegistro}`
        : 'https://www.descubraseusoleos.com.br'

      await fetch(EMAIL_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'curso_aluno',
          nome: registro.nome || '',
          email,
          usuario: usuarioDoRegistro,
          linkCriarSenha,
          linkPersonalizado,
          linkCertificado: `https://www.descubraseusoleos.com.br/certificado/download?email=${encodeURIComponent(email)}&token=${encodeURIComponent(registro.tokenAcesso || '')}`,
          dataExpiracao: registro.dataFimAcesso || '',
          indicadoPorNome: '',
          indicadoPorWhatsapp: '',
        }),
      })

      setReenvioOk(true)
    } catch (err) {
      console.error('[criar-senha] erro ao reenviar link:', err)
      setReenvioErro('Não foi possível enviar o novo link. Tente novamente ou fale com o suporte.')
    } finally {
      setReenviando(false)
    }
  }

  // ── Submit ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!registroId) return
    setErro('')

    if (novaSenha.length < 6) {
      setErro('A senha deve ter pelo menos 6 caracteres.')
      return
    }
    if (!/\d/.test(novaSenha)) {
      setErro('A senha deve conter pelo menos 1 número.')
      return
    }
    if (novaSenha !== confirmarSenha) {
      setErro('As senhas não coincidem.')
      return
    }

    setSalvando(true)
    try {
      const agora = new Date().toISOString()
      const dataExpiracao = new Date(Date.now() + 1000 * 60 * 60 * 24 * 21).toISOString()

      // Buscar conta existente na coleção consultoras (criada antecipadamente na /selecao)
      const respC = await lumi.entities.consultoras.list({
        filter: { email: emailParticipante },
        limit: 1,
      })
      const contaExistente = respC?.list?.[0]

      if (contaExistente) {
        // Atualizar senha e ativar conta
        await lumi.entities.consultoras.update(contaExistente._id, {
          senha: novaSenha.trim(),
          ativo: true,
          cursoConcluido: true,
          updatedAt: agora,
        })
      } else {
        // Criar conta do zero (fallback — não deveria acontecer normalmente)
        await lumi.entities.consultoras.create({
          nome: nomeParticipante,
          usuario: usuarioGerado,
          senha: novaSenha.trim(),
          email: emailParticipante,
          whatsapp: whatsappParticipante,
          pais: 'Brasil',
          ativo: true,
          tipoAcesso: 'capacitacao',
          statusAssinatura: 'capacitacao',
          dataExpiracao,
          cursoConcluido: true,
          exibirOleosResultado: false,
          exibirOleosResultadoKids: false,
          creator: emailParticipante,
          createdAt: agora,
          updatedAt: agora,
        })
      }

      // Atualizar registro de capacitacao
      await lumi.entities.capacitacao.update(registroId, {
        tokenCriarSenha: '',
        tokenCriarSenhaExpiracao: '',
        etapaAtual: 'acesso_criado',
        senhaCriada: true,
        updatedAt: agora,
      })

      setSucesso(true)
      // Redireciona para /admin após 5 segundos
      setTimeout(() => {
        window.location.href = '/admin'
      }, 5000)
    } catch (err) {
      console.error('[criar-senha] erro ao criar senha:', err)
      setErro('Não foi possível criar seu acesso. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  const copiarTexto = async (texto: string, qual: 'diag' | 'cap') => {
    try {
      await copiarParaArea(texto)
      if (qual === 'diag') {
        setCopiouDiag(true)
        setTimeout(() => setCopiouDiag(false), 1500)
      } else {
        setCopiouCap(true)
        setTimeout(() => setCopiouCap(false), 1500)
      }
    } catch {
      // ignore
    }
  }

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2D6A4F]" size={32} />
      </div>
    )
  }

  // ── Token inválido / expirado ──
  if (!tokenValido) {
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg border border-[#EDE0D4]">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] text-center mb-3">
            {tokenExpirado ? 'Link expirado' : 'Link inválido'}
          </h1>
          <p className="text-sm text-[#5F5E5A] text-center mb-6 leading-relaxed">
            {tokenExpirado
              ? 'Este link de criação de senha expirou ou já foi utilizado. Solicite um novo link abaixo.'
              : 'O link de criação de senha é inválido. Verifique se copiou o endereço completo corretamente ou solicite um novo link abaixo.'}
          </p>

          {!reenvioOk ? (
            <div className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-2xl p-5 mb-4">
              <p className="text-sm font-medium text-[#2C2C2A] mb-3 flex items-center gap-2">
                <RefreshCw size={15} className="text-[#2D6A4F]" />
                Solicitar novo link
              </p>
              <input
                type="email"
                value={reenvioEmail}
                onChange={e => setReenvioEmail(e.target.value)}
                placeholder="Seu e-mail da inscrição"
                className="w-full px-4 py-2.5 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none transition-colors text-sm mb-3"
              />
              {reenvioErro && (
                <p className="text-xs text-red-600 mb-3">{reenvioErro}</p>
              )}
              <button
                type="button"
                onClick={solicitarNovoLink}
                disabled={reenviando}
                className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] text-white py-2.5 rounded-full font-medium text-sm hover:bg-[#1f4f3a] transition-all disabled:opacity-60"
              >
                {reenviando ? (
                  <>
                    <Loader2 className="animate-spin" size={15} />
                    Enviando novo link…
                  </>
                ) : (
                  <>
                    <RefreshCw size={15} />
                    Enviar novo link por e-mail
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="bg-[#D8F3DC] border border-[#52B788] rounded-2xl p-4 mb-4 text-center">
              <CheckCircle className="w-6 h-6 text-[#2D6A4F] mx-auto mb-2" />
              <p className="text-sm text-[#2D6A4F] font-medium">
                Novo link enviado para o seu e-mail!
              </p>
              <p className="text-xs text-[#5F5E5A] mt-1">
                Verifique sua caixa de entrada e spam.
              </p>
            </div>
          )}

          <a
            href="https://wa.me/5581988671210"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-full font-medium hover:bg-[#1ebe57] transition-all text-sm"
          >
            Falar com o suporte no WhatsApp
          </a>
          <div className="mt-3 text-center">
            <a href="/certificado" className="text-sm text-[#2D6A4F] hover:underline">
              Voltar ao certificado
            </a>
          </div>
        </div>
      </div>
    )
  }

  // ── Bloco 5 — Sucesso ──
  if (sucesso) {
    const linkDiag = `descubraseusoleos.com.br?c=${usuarioGerado}`
    const linkCap = `descubraseusoleos.com.br/capacitacao?indicado_por=${usuarioGerado}`
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4 py-10">
        <div className="max-w-lg w-full bg-white p-8 rounded-2xl shadow-lg border border-[#EDE0D4]">
          <div className="bg-[#D8F3DC] border border-[#A8DFC4] rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-7 h-7 text-[#2D6A4F] flex-shrink-0 mt-0.5" />
              <h1 className="text-xl sm:text-2xl font-cormorant font-semibold text-[#1F4F3A] leading-tight">
                Acesso criado com sucesso!
              </h1>
            </div>

            {/* Usuário em destaque */}
            <div className="bg-white/80 rounded-xl p-4 mb-4 border border-[#A8DFC4] text-center">
              <p className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1">
                Seu usuário
              </p>
              <p className="text-2xl font-bold text-[#2D6A4F]">{usuarioGerado}</p>
              <p className="text-xs text-[#5F5E5A] mt-1">Guarde esse usuário — você vai precisar dele para entrar no painel.</p>
            </div>

            <p className="text-sm text-[#2C2C2A] mb-4 font-medium">
              Seus links personalizados já estão prontos:
            </p>

            <div className="bg-white/80 rounded-xl p-4 mb-3 border border-[#A8DFC4]">
              <p className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1">
                Link do Diagnóstico
              </p>
              <div className="flex items-center justify-between gap-2">
                <code className="text-sm text-[#2C2C2A] break-all">{linkDiag}</code>
                <button
                  type="button"
                  onClick={() => copiarTexto(`https://${linkDiag}`, 'diag')}
                  className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-[#2D6A4F] hover:text-[#1f4f3a] font-medium"
                >
                  <Copy size={13} />
                  {copiouDiag ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            </div>

            <div className="bg-white/80 rounded-xl p-4 mb-4 border border-[#A8DFC4]">
              <p className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1">
                Link da Capacitação
              </p>
              <div className="flex items-center justify-between gap-2">
                <code className="text-sm text-[#2C2C2A] break-all">{linkCap}</code>
                <button
                  type="button"
                  onClick={() => copiarTexto(`https://${linkCap}`, 'cap')}
                  className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-[#2D6A4F] hover:text-[#1f4f3a] font-medium"
                >
                  <Copy size={13} />
                  {copiouCap ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            </div>

            <p className="text-xs text-[#5F5E5A] leading-relaxed">
              Você encontrará esses links completos no seu painel em <strong>"Como Divulgar seu Link"</strong>.
            </p>
          </div>

          <button
            type="button"
            onClick={() => { window.location.href = '/admin' }}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#2D6A4F] text-white py-3 rounded-full font-medium hover:bg-[#1f4f3a] transition-all"
          >
            Acessar meu painel agora
            <ArrowRight size={18} />
          </button>
          <p className="text-center text-xs text-[#5F5E5A] mt-3">
            Você será redirecionado automaticamente em alguns segundos…
          </p>
        </div>
      </div>
    )
  }

  // ── Formulário ──
  const forca = calcularForcaSenha(novaSenha)
  const corForca = forca === 'forte' ? '#2D6A4F' : forca === 'media' ? '#B08968' : '#D97706'
  const labelForca = forca === 'forte' ? 'Forte' : forca === 'media' ? 'Média' : forca === 'fraca' ? 'Fraca' : ''
  const larguraForca = forca === 'forte' ? '100%' : forca === 'media' ? '66%' : forca === 'fraca' ? '33%' : '0%'
  const linkDiagExibir = `descubraseusoleos.com.br?c=${usuarioGerado}`
  const linkCapExibir = `descubraseusoleos.com.br/capacitacao?indicado_por=${usuarioGerado}`

  return (
    <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4 py-10">
      <div className="max-w-xl w-full bg-white p-7 sm:p-9 rounded-2xl shadow-lg border border-[#EDE0D4]">
        {/* Cabeçalho */}
        <div className="text-center mb-7">
          <div className="w-16 h-16 bg-[#D8F3DC] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#2D6A4F]" />
          </div>
          <p className="text-xs uppercase tracking-wider text-[#B08968] font-medium mb-1">
            Diagnóstico Saúde e Emoções
          </p>
          <h1 className="text-2xl sm:text-3xl font-cormorant font-semibold text-[#2C2C2A] leading-tight">
            Quase lá! Crie sua senha de acesso ao painel
          </h1>
        </div>

        {/* Bloco 1 — Boas-vindas */}
        <p className="text-[15px] text-[#2C2C2A] leading-relaxed mb-6">
          <strong>Parabéns por concluir o curso!</strong> Agora crie sua senha para acessar o Sistema Descubra seus Óleos.
        </p>

        {/* Bloco 2 — Usuário em destaque */}
        <div className="bg-[#D8F3DC] border border-[#A8DFC4] rounded-2xl p-5 mb-4">
          <p className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-2">
            Seu usuário é:
          </p>
          <p className="text-3xl font-bold text-[#2D6A4F] mb-2">{usuarioGerado || '—'}</p>
          <p className="text-sm text-[#5F5E5A] leading-relaxed">
            Esse é seu usuário de acesso ao painel. Guarde essa informação.
          </p>
        </div>

        {/* Bloco 3 — Links personalizados */}
        <div className="bg-[#F8F4EF] border border-[#A8DFC4] rounded-2xl p-5 mb-6">
          <p className="text-sm font-semibold text-[#2D6A4F] mb-3">
            🔗 Seus links personalizados de divulgação já estão prontos:
          </p>
          <div className="mb-3">
            <p className="text-xs font-semibold text-[#2D6A4F] uppercase tracking-wider mb-1">Link do Diagnóstico</p>
            <p className="text-sm text-[#2C2C2A] break-all font-mono">{linkDiagExibir}</p>
          </div>
          <div className="mb-3">
            <p className="text-xs font-semibold text-[#2D6A4F] uppercase tracking-wider mb-1">Link da Capacitação</p>
            <p className="text-sm text-[#2C2C2A] break-all font-mono">{linkCapExibir}</p>
          </div>
          <p className="text-xs text-[#5F5E5A] leading-relaxed mt-2">
            Você encontrará esses links completos no seu painel em <strong>"Como Divulgar seu Link"</strong> após criar sua senha.
          </p>
        </div>

        {/* Bloco 4 — Formulário de senha */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm text-[#2C2C2A] mb-2 font-medium">
              Crie sua senha
            </label>
            <input
              type="password"
              value={novaSenha}
              onChange={e => setNovaSenha(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none transition-colors"
              placeholder="Mínimo 6 caracteres"
              autoComplete="new-password"
            />
            {novaSenha && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-[#5F5E5A]">Força da senha:</span>
                  <span className="text-xs font-medium" style={{ color: corForca }}>{labelForca}</span>
                </div>
                <div className="h-1.5 w-full bg-[#EDE0D4] rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-300"
                    style={{ width: larguraForca, backgroundColor: corForca }}
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-[#2C2C2A] mb-2 font-medium">
              Confirme sua senha
            </label>
            <input
              type="password"
              value={confirmarSenha}
              onChange={e => setConfirmarSenha(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none transition-colors"
              placeholder="Digite a senha novamente"
              autoComplete="new-password"
            />
            {confirmarSenha && novaSenha !== confirmarSenha && (
              <p className="text-xs text-red-600 mt-1.5">As senhas não coincidem</p>
            )}
          </div>

          {erro && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {erro}
            </p>
          )}

          <button
            type="submit"
            disabled={salvando}
            className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] text-white py-3.5 rounded-full font-medium hover:bg-[#1f4f3a] transition-all disabled:opacity-60"
          >
            {salvando ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Criando seu acesso…
              </>
            ) : (
              <>
                Criar senha e acessar o painel
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
